import { XY } from '@goodboydigital/odie';
import { Container, Sprite } from 'pixi.js';

import { Shared } from '../../App';
import { BillboardTutorialAccelerate } from '../../ui/billboard/BillboardTutorialAccelerate';
import { BillboardTutorialReverse } from '../../ui/billboard/BillboardTutorialReverse';
import { BillboardTutorialRotateCCW } from '../../ui/billboard/BillboardTutorialRotateCCW';
import { BillboardTutorialRotateCW } from '../../ui/billboard/BillboardTutorialRotateCW';
import NativeAdPanel from '../../ui/NativeAdPanel';
import { EditorSpriteData } from '../../utils/EditorUtils';
import { CullingComponent } from '../components/CullingComponent';
import { Game } from '../Game';
import { BaseEntity } from './BaseEntity';

export interface BillboardParams
{
    type: string,
    x: number,
    y: number,
    w: number,
    h: number,
    sx: number,
    sy: number,
    r: number,
    props: {
        content: string
    }
}

export class BillboardEntity extends BaseEntity
{
    public base: Sprite;
    public params!: BillboardParams;
    public culling: CullingComponent;
    private content?: string;
    private contentView?: Container;

    constructor()
    {
        super();
        this.culling = this.addComponent(CullingComponent);
        this.base = new Sprite();
        this.base.anchor.set(0.5);
        this.view.addChild(this.base);
    }

    public build(params: BillboardParams): void
    {
        this.params = params;
        this.base.texture = Shared.assets.getTexture('tt-billboard-base');

        this.base.scale.x = this.params.sx;
        this.base.scale.y = this.params.sy;
        this.base.width = this.params.w;
        this.base.height = this.params.h;
        this.x = this.params.x;
        this.y = this.params.y;
        this.z = -0.5;
        this.culling.updateBounds();

        this.refreshContent(this.params.props ? this.params.props.content : '');
        this.view.renderable = false;
    }

    private refreshContent(content: string)
    {
        if (this.content === content) return;
        this.content = content;
        this.name = content;

        if (this.contentView)
        {
            this.contentView.parent.removeChild(this.contentView);
            this.contentView.destroy();
        }

        switch (this.content)
        {
            case 'accelerate':
                this.contentView = new BillboardTutorialAccelerate();
                this.view.addChild(this.contentView);
                break;
            case 'rotate-cw':
                this.contentView = new BillboardTutorialRotateCW();
                this.view.addChild(this.contentView);
                break;
            case 'rotate-ccw':
                this.contentView = new BillboardTutorialRotateCCW();
                this.view.addChild(this.contentView);
                break;
            case 'reverse':
                this.contentView = new BillboardTutorialReverse();
                this.view.addChild(this.contentView);
                break;
        }
    }

    public async hide(): Promise<void>
    {
        this.game?.tween.kill(this);
        await this.game?.tween.to(this, { y: this.params.y + 400, duration: 0.3, ease: 'back.in' });
        this.view.renderable = false;
    }

    public async reveal(): Promise<void>
    {
        this.view.renderable = true;
        this.y = this.params.y + 400;
        this.game?.soundSystem.playSfx('windmill');
        this.game?.tween.kill(this);
        await this.game?.tween.to(this, { y: this.params.y, duration: 0.5, ease: 'back.out' });
    }
}

export class BillboardFactory
{
    public static match(data: EditorSpriteData): boolean
    {
        return data.img !== undefined && data.img.startsWith('billboard');
    }

    public static create(game: Game, params: Partial<BillboardParams>, reveal = false): void
    {
        const billboard = game.pool.get(BillboardEntity) as BillboardEntity;
        const w = 920 * 0.5;
        const h = 700 * 0.5;

        billboard.build({
            type: 'SpriteEntity',
            x: 0,
            y: 0,
            w, h,
            sx: 1,
            sy: 1,
            r: 0,
            props: { content: 'accelerate' },
            ...params,
        });
        game.level.addEntity(billboard);

        reveal ? billboard.reveal() : billboard.hide();
    }

    public static setupLegacyOnboarding(game: Game, xy: XY, spacing = 500): void
    {
        this.create(game, {
            x: xy.x,
            y: xy.y,
            props: { content: 'accelerate' },
        });

        this.create(game, {
            x: xy.x + spacing,
            y: xy.y,
            props: { content: 'rotate' },
        });

        this.create(game, {
            x: xy.x + (spacing * 2),
            y: xy.y,
            props: { content: 'brake' },
        });
    }
}
